/**
 * Presence — who is on the storefront right now, as the last poll read it.
 * Pure state: whichever `presence.sync.ts` is mounted is its sole writer — the
 * storefront's against the buyer documents, the studio's against fixtures —
 * and it runs only while a surface that shows this is mounted.
 *
 * The window and the cadence live here rather than in the reconciler because
 * they are what the figure MEANS, not how it is fetched: "here right now" is
 * a claim about a 75-second window, and a surface reading the cell inherits
 * that claim whether or not it prints it. A reconciler owning them would let
 * a second reconciler answer the same cell on a different window.
 *
 * ONE QUERY, TWO READINGS. The per-event split is the org-wide hero's own
 * segments, one per event; the console's band takes the one event it is
 * standing on out of that same split. Both come out of the one query the poll
 * already makes.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { consoleEventId } from "../events/console/console.state.ts";
import { eventsState } from "../events/events.state.ts";

/** How far back a beat still counts as "here". Two missed beats plus slack:
 *  a visible tab beats every thirty seconds, so a visitor whose last beat is
 *  inside this window is one whose tab has not gone quiet. */
export const PRESENCE_WINDOW_MS = 75_000;

/** How often the panel re-asks. Inside the window above, so the figure is
 *  never more than one poll stale against a signal already ±75 s coarse. */
export const PRESENCE_POLL_MS = 20_000;

export type LivePresence = {
	readonly total: number;
	/** Buyers in the window by the event whose page they are on. Only an
	 *  event page names one, so these never sum to the total — the rest are
	 *  on the home page, at an order, or reading the legal texts. */
	readonly byEvent: Readonly<Record<string, number>>;
	/** Buyers in the window by the surface they are standing on. Every visitor
	 *  is on exactly one, so these DO sum to the total — which is what lets the
	 *  site-wide figure decompose into where everyone actually is. */
	readonly byPage: Readonly<Record<string, number>>;
};

/** Zero is a real answer and renders at the same scale as any other — the
 *  bar beneath is what swaps, never the figure's slot. Null until a poll has
 *  answered for this organization, which is no count at all. */
export const livePresence = state<LivePresence | null>(
	"admin/live-presence",
).memory(null);

/** The live figure per event, named as the operator names them and ordered
 *  by size — the org-wide hero's own segments. An event nobody is on is not a
 *  segment; an event the catalog no longer lists falls out with its name,
 *  since a bare id in a legend names nothing a reader recognises. */
export const livePresenceByEvent = computed<
	readonly {
		readonly eventId: string;
		readonly label: string;
		readonly count: number;
	}[]
>(() =>
	Object.entries(livePresence.value?.byEvent ?? {})
		.flatMap(([eventId, count]) => {
			const event = eventsState.value.find(
				(candidate) => candidate.id === eventId,
			);
			return count > 0 && event !== undefined
				? [{ eventId, label: event.name, count }]
				: [];
		})
		.toSorted((a, b) => b.count - a.count),
);

/** The console's own reading: how many buyers are on the event the console is
 *  standing on. The rest of the shop is not this cell's subject — the band
 *  prints one figure and it answers one question — so the remainder is not
 *  derived here for a caller to print anyway. Its own figure rather than the
 *  raw cell, because keying `byEvent` on the scoped event is the reading, and
 *  a view that did it would be the one place it could be done differently. */
export const consolePresence = computed<{ readonly onEvent: number } | null>(
	() =>
		livePresence.value === null
			? null
			: {
					onEvent: livePresence.value.byEvent[consoleEventId.value ?? ""] ?? 0,
				},
);
